import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { EstablishmentCreate, EstablishmentUpdate, Invoice } from '@/lib/schemas';

// ============================================================================
// Query Hooks (Phase 9.18d: Organization Establishments)
// ============================================================================

/**
 * Fetches all establishments (DBAs) for a specific organization
 */
export function useOrganizationEstablishments(
  organizationId: string | undefined,
  options?: Omit<UseQueryOptions<any, Error, any, any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['organization-establishments', organizationId],
    queryFn: () => api.organizationEstablishments.list(organizationId!),
    enabled: !!organizationId,
    ...options,
  });
}

// ============================================================================
// Mutation Hooks (Phase 9.18d: Establishment CRUD operations)
// ============================================================================

/**
 * Creates a new establishment for an organization.
 *
 * Phase 9.23a: Added legal_name support.
 * v0.12.26: Added address + hide_dba_default support so users can set them
 * at create-time.
 *
 * v0.14 (2026-05-29): Pass the create payload through whole instead of
 * explicit-picking individual fields. Explicit-pick silently drops new
 * fields added to EstablishmentCreate (cross-cutting hunt R3 +
 * feedback_mutation_hook_field_drift.md). The schema is the single source
 * of truth for what the API accepts.
 */
export function useCreateOrganizationEstablishment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      ...data
    }: { organizationId: string } & EstablishmentCreate) =>
      api.organizationEstablishments.create(organizationId, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organization-establishments', variables.organizationId] });
      toast.success('Establishment added successfully');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add establishment');
    },
  });
}

/**
 * Updates an establishment
 */
export function useUpdateOrganizationEstablishment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationId, establishmentId, data }: {
      organizationId: string;
      establishmentId: number;
      data: EstablishmentUpdate;
    }) =>
      api.organizationEstablishments.update(organizationId, establishmentId, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organization-establishments', variables.organizationId] });
      toast.success('Establishment updated successfully');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update establishment');
    },
  });
}

/**
 * R3 (invoice-identity-correctable) AC6 — preview the blast radius of correcting
 * an establishment's legal name. The legal entity rides with the DBA, so every
 * invoice that points at this establishment (with no per-invoice override)
 * may render this legal name. Returns the exact LIST
 * of affected invoice NUMBERS so the admin/establishment screen can show Christa
 * exactly which invoices change BEFORE the cascade write commits.
 *
 * The cascade WRITE itself is the existing establishment PUT
 * (useUpdateOrganizationEstablishment); this hook is only the pre-commit
 * PREVIEW. It reads every page of the tenant's invoice list and excludes rows
 * whose rendered name will remain the same. Numbers are returned ASC.
 */
const nonEmpty = (value: string | null | undefined) => value?.trim() || '';

// This is a rare, explicit confirmation action. Read every page so a busy
// client cannot silently lose invoice numbers after the first 1,000.
async function allInvoicesForIdentityPreview(): Promise<Invoice[]> {
  const invoices: Invoice[] = [];
  for (;;) {
    const page = await api.invoices.list({ limit: 1000, offset: invoices.length });
    if (page.invoices.length === 0 && invoices.length < page.total) {
      throw new Error('Invoice preview was incomplete');
    }
    invoices.push(...page.invoices);
    if (invoices.length >= page.total) return invoices;
  }
}

function changedNumbers(invoices: Invoice[], changes: (invoice: Invoice) => boolean): string[] {
  return invoices
    .filter((invoice) => invoice.invoice_number && !nonEmpty(invoice.frozen_legal_name) && changes(invoice))
    .map((invoice) => invoice.invoice_number)
    .sort((a, b) => a.localeCompare(b));
}

export function useEstablishmentAffectedInvoices() {
  return useCallback(async (establishmentId: number, newLegalName: string): Promise<string[]> => {
    const invoices = await allInvoicesForIdentityPreview();
    return changedNumbers(invoices, (invoice) => {
      if (invoice.establishment_id !== establishmentId) return false;
      const before = nonEmpty(invoice.client_legal_name) || nonEmpty(invoice.client_name);
      const after = nonEmpty(newLegalName) || nonEmpty(invoice.org_legal_name) || nonEmpty(invoice.client_name);
      return before !== after;
    });
  }, []);
}

/**
 * Row-7 (invoice-identity-correctable): preview the invoices a CLIENT (org)
 * legal-name change affects. The org form's "Save Changes" writes
 * organizations.legal_name, which cascades to the LIVE render of every invoice
 * under this org that resolves its legal name from o.legal_name
 * (COALESCE(est.legal_name, o.legal_name, o.name)) — SENT invoices included, and
 * with no warning before this fix. Christa must never cascade-change issued
 * invoices unknowingly.
 *
 * Only invoices without a DBA legal name or per-invoice override can change.
 * Preview the actual rendered-name difference so the warning never implies
 * that unrelated invoices under the same client will be rewritten.
 */
export function useOrganizationAffectedInvoices() {
  return useCallback(async (organizationId: string, newLegalName: string): Promise<string[]> => {
    const invoices = await allInvoicesForIdentityPreview();
    return changedNumbers(invoices, (invoice) => {
      if (invoice.organization_id !== organizationId || nonEmpty(invoice.establishment_legal_name)) return false;
      const before = nonEmpty(invoice.client_legal_name) || nonEmpty(invoice.client_name);
      const after = nonEmpty(newLegalName) || nonEmpty(invoice.client_name);
      return before !== after;
    });
  }, []);
}

/**
 * Deletes an establishment
 */
export function useDeleteOrganizationEstablishment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationId, establishmentId }: { organizationId: string; establishmentId: number }) =>
      api.organizationEstablishments.delete(organizationId, establishmentId),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organization-establishments', variables.organizationId] });
      toast.success('Establishment deleted successfully');
      return response;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete establishment');
    },
  });
}
